import React, { useState } from 'react';
import { Card, Typography, Grid, Tooltip } from 'antd';
import { UserOutlined, EditOutlined, ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { logEvent } from '../../analytics';
import GenerateDocumentAndRedirectButton from '../patient/GenerateDocumentAndRedirectButton';
import PatientForm from '../patient/PatientForm';
import LinkButton from '../buttons/LinkButton';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const NoteViewPatient = ({ patient: initialPatient, onPatientUpdated }) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [patient, setPatient] = useState(initialPatient);

  const cardTitle = (
    <>
      {patient.getFullName().length > 15 ? (
        <Tooltip title={patient.getFullName()}>
          <span>
            <UserOutlined style={{ marginRight: 8 }} />
            {patient.getFullName()}
          </span>
        </Tooltip>
      ) : (
        <>
          <UserOutlined style={{ marginRight: 8 }} />
          {patient.getFullName()}
        </>
      )}
    </>
  );

  const cardExtra = (
    <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
      <LinkButton
        onClick={() => {
          logEvent('NotePatientWidget', 'Edit');
          setIsEditModalOpen(true);
        }}
        buttonProps={{
          icon: <EditOutlined />
        }}
      >
        {t('common.edit')}
      </LinkButton>
      <LinkButton
        onClick={() => {
          logEvent('NotePatientWidget', 'ViewProfile');
          window.open(`/patients/${patient.getUUID()}`, '_blank');
        }}
        buttonProps={{
          icon: <ExportOutlined />,
          'data-testid': 'view-profile-button'
        }}
      >
        {t('session_details_patient.view_profile')}
      </LinkButton>
    </div>
  );

  const getDemographicsLine = () => {
    const parts = [];
    if (patient.getGenderSubject()) {
      parts.push(patient.getGenderSubject());
    }
    if (patient.getAge()) {
      parts.push(patient.getAgeDisplay());
    }
    if (patient.getCivilStatus()) {
      parts.push(patient.getCivilStatus());
    }
    return parts.join(' • ');
  };

  const getLocationLine = () => {
    const parts = [];
    if (patient.getLocation()) {
      parts.push(
        <>
          <Text type="secondary">{t('patient_form.fields.location_prefix')} </Text>
          {patient.getLocation()}
        </>
      );
    }
    if (patient.getOccupation()) {
      parts.push(patient.getOccupation());
    }
    if (patient.getReligion()) {
      parts.push(patient.getReligion());
    }
    return parts.map((part, index) => (
      <React.Fragment key={index}>
        {index > 0 && ' • '}
        {part}
      </React.Fragment>
    ));
  };

  const getBMIDisplay = () => {
    const bmi = patient.getCalculatedBMI();
    if (!bmi) return null;

    return (
      <>
        <Text type="secondary">{t('patient_form.fields.bmi')}: </Text>
        {bmi}
      </>
    );
  };

  const getPhysicalLine = () => {
    const parts = [];
    if (patient.getWeight() || patient.getHeight() || patient.getBMI() || patient.getBloodType()) {
      const measurements = [
        patient.getWeightDisplay(),
        patient.getHeightDisplay(),
        getBMIDisplay(),
        patient.getBloodType()
      ].filter(Boolean);

      parts.push(
        <>
          <Text type="secondary">{t('patient_form.fields.measurements')}: </Text>
          {measurements.map((measurement, index) => (
            <React.Fragment key={index}>
              {index > 0 && ' • '}
              {measurement}
            </React.Fragment>
          ))}
        </>
      );
    }
    return parts;
  };

  const handleSave = (updatedPatient) => {
    setPatient(updatedPatient);
    setIsEditModalOpen(false);
    if (onPatientUpdated) {
      onPatientUpdated(updatedPatient);
    }
  };

  return (
    <>
      <Card
        size='default'
        title={cardTitle}
        extra={cardExtra}
        style={{ marginBottom: 24 }}
      >
        <div data-testid="patient-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {getDemographicsLine() && (
            <Text>{getDemographicsLine()}</Text>
          )}
          {getLocationLine().length > 0 && (
            <Text>{getLocationLine()}</Text>
          )}
          {getPhysicalLine().length > 0 && (
            <Text>{getPhysicalLine()}</Text>
          )}
        </div>
        {(patient.getCompletedSessionsCount() || patient.getCompletedCallsCount()) && (
          <div style={{ marginTop: 20 }}>
            <GenerateDocumentAndRedirectButton patient={patient} />
          </div>
        )}
      </Card>

      <PatientForm
        isOpen={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onSave={handleSave}
        editingPatient={patient}
      />
    </>
  );
};

export default NoteViewPatient; 